import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { HelpCircle, MessageCircle } from 'lucide-react';

const HPFAQSection = () => {
  return (
    <section id="hp-faq" className="py-20 lg:py-28 bg-background">
      <div className="section-container">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left Column - Header */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <span className="inline-block text-sm font-semibold text-accent uppercase tracking-wider mb-4">
                Got Questions?
              </span>
              <h3 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-6">
                HP Finance FAQs
              </h3>
              <p className="text-muted-foreground leading-relaxed mb-8">
                Find answers to common questions about HP car finance mis-selling claims. 
                Cannot find what you are looking for? Get in touch with our team.
              </p>
              
              {/* Contact Card */}
              <div className="p-6 rounded-2xl bg-muted/50 border border-border">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-foreground">
                      Still have questions?
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Speak to our team for personalised advice
                    </p>
                  </div>
                </div>
                <Button variant="outline" className="w-full" asChild>
                  <a href="/contact">Contact Us</a>
                </Button>
              </div>
            </div>
          </div>

          {/* Right Column - FAQ Accordion */}
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem
                value="hp-item-1"
                className="bg-card border border-border rounded-xl px-6 data-[state=open]:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:no-underline py-5">
                  <div className="flex items-start gap-4">
                    <HelpCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>What is HP car finance mis-selling?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pl-9 pb-5">
                  Hire Purchase (HP) is one of the most common ways to finance a car in the UK, but millions of agreements were mis-sold by dealerships who failed to disclose the commissions they were earning. Using a practice known as Discretionary Commission Arrangements (DCAs), brokers and dealers were able to secretly increase the interest rate on your HP agreement, earning themselves a larger commission while you paid more than you should have. The FCA found that commission was paid on around 95% of UK car finance agreements, and that as many as 40% may have been mis-sold. DCAs were banned by the FCA in January 2021, but agreements going back to April 2007 are covered by the forthcoming compensation scheme.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                value="hp-item-2"
                className="bg-card border border-border rounded-xl px-6 data-[state=open]:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:no-underline py-5">
                  <div className="flex items-start gap-4">
                    <HelpCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>How is HP mis-selling different from PCP mis-selling?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pl-9 pb-5">
                  With HP finance, you make fixed monthly payments over an agreed term and own the car outright at the end. With PCP, you have a large optional final payment. However, the mis-selling issue is the same for both: dealers and brokers were using hidden commission arrangements to inflate the interest rate you were charged without telling you. Both HP and PCP agreements are covered by the FCA's proposed compensation scheme for agreements taken out between 6 April 2007 and 1 November 2024. If you had an HP agreement during this period, you have the same right to claim as PCP customers.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                value="hp-item-3"
                className="bg-card border border-border rounded-xl px-6 data-[state=open]:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:no-underline py-5">
                  <div className="flex items-start gap-4">
                    <HelpCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>How do I know if my HP agreement was mis-sold?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pl-9 pb-5">
                  Your HP agreement may have been mis-sold if any of the following apply: you were not told that the dealer or broker was earning a commission from arranging your finance; the interest rate on your agreement was set higher than necessary so that the dealer could earn more commission; you were not shown alternative finance options or lenders; the total cost of the agreement, including all interest and charges, was not clearly explained to you; or affordability checks were not properly carried out before the agreement was approved. You do not need to have known this was happening at the time — the mis-selling was by its nature hidden from you.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                value="hp-item-4"
                className="bg-card border border-border rounded-xl px-6 data-[state=open]:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:no-underline py-5">
                  <div className="flex items-start gap-4">
                    <HelpCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>How much compensation could I receive for a mis-sold HP agreement?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pl-9 pb-5">
                  The FCA has indicated that average compensation is likely to be in the region of £830 per agreement, calculated based on the excess interest you were charged as a result of the hidden commission arrangement. The actual amount will depend on the size of your HP agreement, the interest rate you were charged, and the length of your finance term. If you financed multiple cars on HP agreements between 2007 and 2024, you may be able to make a separate claim for each one, significantly increasing the total compensation you could receive.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                value="hp-item-5"
                className="bg-card border border-border rounded-xl px-6 data-[state=open]:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:no-underline py-5">
                  <div className="flex items-start gap-4">
                    <HelpCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Do I need my HP finance documents to make a claim?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pl-9 pb-5">
                  No, you do not need to have kept your original HP agreement documents. Our system can trace your car finance agreements going back to April 2007 using just your name, date of birth and current address. Many people do not remember the exact details of their HP agreement, including who the lender was or what interest rate they were charged, and that is entirely normal given how long ago some of these agreements were taken out. We have the tools to locate this information on your behalf and handle the process from start to finish.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                value="hp-item-6"
                className="bg-card border border-border rounded-xl px-6 data-[state=open]:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:no-underline py-5">
                  <div className="flex items-start gap-4">
                    <HelpCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>How long does an HP mis-selling claim take?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pl-9 pb-5">
                  The FCA has put in place a complaints pause until 31 May 2026, which means lenders are not currently required to issue final responses on motor finance complaints while the compensation scheme is being finalised. This does not affect your ability to register your claim now — and registering early is important, as those who have already submitted a complaint are likely to be among the first to receive compensation once the scheme launches. The FCA's final rules are expected in late March 2026, with the scheme expected to begin processing payouts from mid-2026 onwards. Blue Lion Law will keep you fully updated throughout the process so you always know where your claim stands.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
};

export { HPFAQSection };
